/* MILES — motion layer.
   Vanilla JS, no dependencies. Everything here is progressive enhancement:
   content is fully readable without it, and it does nothing (beyond marking
   the page ready) for visitors who prefer reduced motion. */
(function () {
  'use strict';

  var doc = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var editor = doc.classList.contains('shopify-design-mode');

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Horizon scrolls .page-wrapper on desktop and the document on mobile. */
  function getScroller() {
    var pw = document.querySelector('.page-wrapper');
    if (pw) {
      var oy = getComputedStyle(pw).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && pw.scrollHeight > pw.clientHeight + 2) return pw;
    }
    return null;
  }

  /* ---------- Split headings into masked words ---------- */
  function splitWords(el) {
    if (el.dataset.lxSplitDone) return 0;
    el.dataset.lxSplitDone = '1';
    var count = 0;
    function walk(node) {
      var children = Array.prototype.slice.call(node.childNodes);
      children.forEach(function (child) {
        if (child.nodeType === 3) {
          var parts = child.textContent.split(/(\s+)/);
          var frag = document.createDocumentFragment();
          parts.forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            var outer = document.createElement('span');
            outer.className = 'lx-w';
            var inner = document.createElement('span');
            inner.className = 'lx-w__i';
            inner.style.setProperty('--lx-w', count++);
            inner.textContent = part;
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR' && child.tagName !== 'svg') {
          walk(child);
        }
      });
    }
    walk(el);
    el.classList.add('lx-split');
    return count;
  }

  /* ---------- Count-up numbers ---------- */
  function formatIN(n) {
    try { return n.toLocaleString('en-IN'); } catch (e) { return String(n); }
  }
  function countUp(el) {
    if (el.dataset.lxCounted) return;
    el.dataset.lxCounted = '1';
    var raw = el.getAttribute('data-lx-count') || el.textContent;
    var target = parseInt(String(raw).replace(/[^\d]/g, ''), 10);
    if (!target || target < 10) return;
    var useComma = /,/.test(el.textContent) || target >= 1000;
    var dur = Math.min(1800, 800 + target * 0.5);
    var start = null;
    function tick(ts) {
      if (!start) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 4);
      var v = Math.round(target * eased);
      el.textContent = useComma ? formatIN(v) : String(v);
      if (p < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ---------- Product pages: "On this page" navigator from the article's h2s ---------- */
  function buildToc() {
    var body = document.querySelector('.miles-product-body');
    if (!body || body.dataset.lxToc) return;
    var heads = Array.prototype.slice.call(body.querySelectorAll('h2'));
    if (heads.length < 3) return;
    body.dataset.lxToc = '1';
    var used = {};
    var nav = document.createElement('nav');
    nav.className = 'lx-toc';
    nav.setAttribute('aria-label', 'On this page');
    var label = document.createElement('span');
    label.className = 'lx-toc__label';
    label.textContent = 'On this page';
    var list = document.createElement('ul');
    list.className = 'lx-toc__list';
    list.setAttribute('role', 'list');
    var links = [];
    heads.forEach(function (h) {
      var base = (h.textContent || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section';
      var id = h.id || base;
      while (used[id] || (!h.id && document.getElementById(id))) id = base + '-' + (used[base] = (used[base] || 1) + 1);
      used[id] = 1;
      h.id = id;
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + id;
      a.textContent = h.textContent.replace(/\?$/, '').trim();
      li.appendChild(a);
      list.appendChild(li);
      links.push(a);
    });
    nav.appendChild(label);
    nav.appendChild(list);
    body.parentNode.insertBefore(nav, body);

    if (typeof IntersectionObserver === 'undefined') return;
    var tio = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        links.forEach(function (a) { a.classList.toggle('is-active', a.getAttribute('href') === '#' + entry.target.id); });
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    heads.forEach(function (h) { tio.observe(h); });
  }

  ready(function () {
    doc.classList.add('lx-ready');
    buildToc();

    var scroller = getScroller();
    var scrollTarget = scroller || window;
    function scrollTop() { return scroller ? scroller.scrollTop : window.pageYOffset; }
    function scrollMax() {
      var el = scroller || document.scrollingElement;
      return Math.max(1, el.scrollHeight - el.clientHeight);
    }

    /* Elements waiting to be revealed. The IntersectionObserver handles normal
       scrolling; catchUp() handles fast flicks, anchor jumps and restored scroll
       positions, where an element can pass the viewport without ever being seen. */
    var pending = [];
    var io = null;
    var lastCatch = 0;

    function reveal(el) {
      if (el.classList.contains('lx-in')) return;
      el.classList.add('lx-in');
      if (el.hasAttribute('data-lx-count')) countUp(el);
      if (el.querySelectorAll) el.querySelectorAll('[data-lx-count]').forEach(countUp);
      if (io) io.unobserve(el);
      var idx = pending.indexOf(el);
      if (idx > -1) pending.splice(idx, 1);
      /* Once revealed, hand the element back to its own hover transitions. */
      var i = parseFloat(el.style.getPropertyValue('--lx-i')) || 0;
      if (el.getAttribute('data-lx') !== 'words') {
        window.setTimeout(function () { el.removeAttribute('data-lx'); }, 1000 + i * 70);
      }
    }

    function catchUp() {
      if (!pending.length) return;
      var line = window.innerHeight * 0.94;
      pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < line) reveal(el);
      });
    }

    /* ---------- Header state + progress line (cheap, rAF-throttled) ---------- */
    var progress = document.querySelector('.lx-progress span');
    var ticking = false;
    var lastY = scrollTop();
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var y = scrollTop();
        var delta = y - lastY;
        doc.classList.toggle('lx-scrolled', y > 40);
        /* Only hide/show the header on a deliberate scroll, not on tiny jitters. */
        if (delta > 8 && y > 480) doc.classList.add('lx-scroll-down');
        else if (delta < -8 || y < 480) doc.classList.remove('lx-scroll-down');
        if (Math.abs(delta) > 8) lastY = y;
        if (progress) progress.style.transform = 'scaleX(' + Math.min(1, y / scrollMax()) + ')';
        var now = Date.now();
        if (now - lastCatch > 120) { lastCatch = now; catchUp(); }
        ticking = false;
      });
    }
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      var next = getScroller();
      if (next !== scroller) {
        scrollTarget.removeEventListener('scroll', onScroll);
        scroller = next;
        scrollTarget = scroller || window;
        scrollTarget.addEventListener('scroll', onScroll, { passive: true });
      }
      onScroll();
    });
    onScroll();

    /* Smooth in-page anchor scrolling inside whichever element scrolls. */
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      /* Section wrappers are "shopify-section-template--<id>__<key>"; allow #<key>. */
      var target = document.getElementById(id) || document.querySelector('[id$="__' + id + '"]');
      if (!target || a.closest('.miles-books')) return;
      e.preventDefault();
      var header = document.querySelector('.miles-header');
      var offset = header ? header.offsetHeight + 12 : 0;
      var top = target.getBoundingClientRect().top + scrollTop() - offset;
      (scroller || window).scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
    });

    if (reduce || editor) {
      doc.classList.add('lx-static');
      return;
    }

    doc.classList.add('lx-motion');

    /* ---------- Hero title ---------- */
    var heroTitle = document.querySelector('[data-lx-split]');
    if (heroTitle) {
      splitWords(heroTitle);
      requestAnimationFrame(function () { heroTitle.classList.add('lx-in'); });
    }

    /* ---------- Auto-tag reveal targets across every page ---------- */
    var headingSel = [
      '.miles-heading-group h2',
      '.lx-split-head h2',
      '.miles-page-hero h1',
      '.miles-claims-hero__title',
      '.miles-cta-banner h2',
      '.lx-footer__big',
      '.lx-stats__statement'
    ].join(',');

    var groups = [
      { sel: '.miles-heading-group > .miles-eyebrow, .miles-heading-group > p, .lx-split-head__aside', fx: 'up' },
      { sel: '.miles-grid > *, .miles-why-grid > *, .miles-benefit-grid > *, .miles-stats-grid > *', fx: 'up', stagger: true },
      { sel: '.miles-stepper__step', fx: 'up', stagger: true },
      { sel: '.miles-faq__item, .miles-office-item, .miles-portfolio-card, .miles-leadership-card', fx: 'up', stagger: true },
      { sel: '.miles-cta-banner, .miles-reviews__viewport, .miles-clients__marquee, .miles-certs__marquee, .miles-journey, .miles-corporate > *', fx: 'fade' },
      { sel: '.miles-books__book-frame', fx: 'up', stagger: true },
      { sel: '.miles-related__list > li, .lx-pindex__group, .lx-toc', fx: 'up', stagger: true },
      { sel: '.miles-footer__top > *', fx: 'up', stagger: true }
    ];

    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });

    function watch(el) {
      if (pending.indexOf(el) === -1) pending.push(el);
      io.observe(el);
    }

    /* Anything already on screen when this (deferred) script runs has been
       painted — hiding it now would flash. Those get CSS-only entrances.
       All geometry is read in ONE pass before any writes. */
    var foldLine = window.innerHeight * 0.92;
    var candidates = [];

    document.querySelectorAll(headingSel).forEach(function (h) {
      if (h.closest('[data-lx-hero]')) return;
      candidates.push({ el: h, heading: true });
    });

    groups.forEach(function (g) {
      document.querySelectorAll(g.sel).forEach(function (el) {
        if (el.classList.contains('miles-reveal') || el.closest('[data-lx-hero]')) return;
        var idx = null;
        if (g.stagger && el.parentElement) {
          idx = Array.prototype.indexOf.call(el.parentElement.children, el);
        }
        candidates.push({ el: el, fx: g.fx, idx: idx, preset: el.hasAttribute('data-lx') });
      });
    });

    /* Read phase */
    candidates.forEach(function (c) {
      c.above = c.el.getBoundingClientRect().top < foldLine;
    });

    /* Write phase */
    candidates.forEach(function (c) {
      var el = c.el;
      if (c.heading) {
        if (c.above) return;
        splitWords(el);
        el.setAttribute('data-lx', 'words');
        watch(el);
        return;
      }
      if (!c.preset && c.above) return;
      if (!el.hasAttribute('data-lx')) el.setAttribute('data-lx', c.fx);
      if (c.idx != null && !el.style.getPropertyValue('--lx-i')) el.style.setProperty('--lx-i', c.idx % 6);
      watch(el);
    });

    document.querySelectorAll('[data-lx]').forEach(function (el) {
      if (!el.closest('[data-lx-hero]') && !el.classList.contains('lx-in')) watch(el);
    });

    /* Stat numbers on existing sections (About, Claims) */
    document.querySelectorAll('.miles-stat-tile__number').forEach(function (el) {
      if (/^\s*\d[\d,]*\+?\s*$/.test(el.textContent)) {
        var suffix = /\+/.test(el.textContent) ? '+' : '';
        var n = el.textContent.replace(/[^\d]/g, '');
        if (n.length === 4 && /^(19|20)/.test(n)) return; /* a year, not a count */
        el.innerHTML = '<span data-lx-count="' + n + '">' + el.textContent.replace('+', '').trim() + '</span>' + suffix;
      }
    });
    document.querySelectorAll('[data-lx-count]').forEach(function (el) {
      if (!el.closest('[data-lx]')) watch(el);
    });

    /* Reveal anything a restored scroll position or anchor jump has already passed. */
    window.setTimeout(catchUp, 400);
    window.addEventListener('load', catchUp);
    window.addEventListener('hashchange', function () { window.setTimeout(catchUp, 60); });

    /* ---------- Pointer niceties (desktop only) ---------- */
    if (!finePointer) return;

    /* Spotlight that follows the cursor across cards */
    var spotSel = '.miles-card, .lx-bento__card, .lx-bento__mini, .miles-why-card, .miles-reviews__card, .miles-cta-banner, .miles-portfolio-card';
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest && e.target.closest(spotSel);
      if (!card) return;
      var r = card.getBoundingClientRect();
      card.style.setProperty('--mx', (e.clientX - r.left) + 'px');
      card.style.setProperty('--my', (e.clientY - r.top) + 'px');
    }, { passive: true });

    /* Magnetic primary buttons — subtle, eased back on leave */
    document.querySelectorAll('.lx-magnetic, .miles-btn-primary, .miles-btn-on-dark').forEach(function (btn) {
      var raf = null;
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var x = (e.clientX - (r.left + r.width / 2)) / r.width;
        var y = (e.clientY - (r.top + r.height / 2)) / r.height;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          btn.style.transform = 'translate(' + (x * 6).toFixed(2) + 'px,' + (y * 4).toFixed(2) + 'px)';
        });
      });
      btn.addEventListener('pointerleave', function () {
        if (raf) cancelAnimationFrame(raf);
        btn.style.transform = '';
      });
    });

    /* Hero parallax — the whole visual drifts, glass cards drift by depth */
    var hero = document.querySelector('[data-lx-hero]');
    var visual = hero && hero.querySelector('[data-lx-parallax]');
    if (hero && visual) {
      var depthEls = visual.querySelectorAll('[data-lx-depth]');
      var tx = 0, ty = 0, cx = 0, cy = 0, running = false, heroVisible = true;
      new IntersectionObserver(function (en) { heroVisible = en[0].isIntersecting; }).observe(hero);
      function loop() {
        cx += (tx - cx) * 0.06;
        cy += (ty - cy) * 0.06;
        visual.style.transform = 'translate3d(' + (cx * 10).toFixed(2) + 'px,' + (cy * 8).toFixed(2) + 'px,0)';
        depthEls.forEach(function (el) {
          var d = parseFloat(el.getAttribute('data-lx-depth')) || 0;
          el.style.translate = (cx * d).toFixed(2) + 'px ' + (cy * d).toFixed(2) + 'px';
        });
        if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) requestAnimationFrame(loop);
        else running = false;
      }
      hero.addEventListener('pointermove', function (e) {
        if (!heroVisible) return;
        var r = hero.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width - 0.5;
        ty = (e.clientY - r.top) / r.height - 0.5;
        if (!running) { running = true; requestAnimationFrame(loop); }
      }, { passive: true });
      hero.addEventListener('pointerleave', function () {
        tx = 0; ty = 0;
        if (!running) { running = true; requestAnimationFrame(loop); }
      });
    }
  });
})();
